import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
	ApplicationPaths,
	LoginActions,
	QueryParameterNames,
} from "./ApiAuthorizationConstants";
import {
	Backdrop,
	Button,
	CircularProgress,
	Grid,
	Typography,
	TextField,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	FormControl,
	FormHelperText
} from "@mui/material";
import { AuthenticationResultStatus } from "./AuthorizeService";
import authService from "./AuthorizeService";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

const Login = ({ action }) => {
	const [message, setMessage] = useState(undefined);
	const [open, setOpen] = useState(true);
	const [formData, setFormData] = useState({
		email: "",
		password: "",
	});

	// Helper functions
	const modifyUrl = (state) => {
		if (state !== undefined) {
			let url = state.split("/");
			if (url.length === 4 && url[3] === "") return state + "Dashboard";
			return state;
		}
	};

	const getReturnUrl = (state) => {
		const params = new URLSearchParams(window.location.search);
		const fromQuery = params.get(QueryParameterNames.ReturnUrl);

		if (fromQuery && !fromQuery.startsWith(`${window.location.origin}/`)) {
			throw new Error(
				"Invalid return url. The return url needs to have the same origin as the current page."
			);
		}

		return (
			(state && state.returnUrl) ||
			fromQuery ||
			`${window.location.origin}/`
		);
	};

	const redirectToApiAuthorizationPath = (apiAuthorizationPath) => {
		const redirectUrl = `${window.location.origin}/${apiAuthorizationPath}`;
		window.location.replace(redirectUrl);
	};

	const redirectToRegister = () => {
		const registerPath = `${ApplicationPaths.IdentityRegisterPath}?${
			QueryParameterNames.ReturnUrl
		}=${encodeURI(ApplicationPaths.Login)}`;
		redirectToApiAuthorizationPath(registerPath);
	};

	const redirectToProfile = () => {
		redirectToApiAuthorizationPath(ApplicationPaths.IdentityManagePath);
	};

	const navigateToReturnUrl = (returnUrl) => {
		window.location.replace(returnUrl);
	};

	const [learnHowUrl, setLearnHowUrl] = useState("");

	// When the component mounts, act based on the provided "action"
	useEffect(() => {
		const getLearnHowUrl = async () => {
			const response = await fetch(
				"api/configuration/getConfigValueByKey/learnHowUrl"
			);
			const data = await response.json();
			return data?.value || "";
		};

		switch (action) {
			case LoginActions.Login:
				// No additional work here.
				break;
			case LoginActions.LoginCallback:
				break;
			case LoginActions.LoginFailed: {
				const params = new URLSearchParams(window.location.search);
				const error = params.get(QueryParameterNames.Message);
				setMessage(error);
				break;
			}
			case LoginActions.Profile:
				redirectToProfile();
				break;
			case LoginActions.Register:
				redirectToRegister();
				break;
			default:
				throw new Error(`Invalid action '${action}'`);
		}

		getLearnHowUrl().then((url) => setLearnHowUrl(url));
	}, []);

	const handleSubmit = async (event) => {
		event.preventDefault();

		try {
			setShowBackdrop(true);
			const result = await authService.signIn({
				email: formData.email,
				password: formData.password,
			});
	
			switch (result.status) {
				case AuthenticationResultStatus.Redirect:
					break;
				case AuthenticationResultStatus.Success:
					navigateToReturnUrl(getReturnUrl());
					break;
				case AuthenticationResultStatus.Fail:
					setMessage(result.message);
					break;
				default:
					throw new Error(`Invalid status result ${result.status}.`);
			}
		}

		catch (error) {
			console.error(error);
			setMessage(error.response.data.message);
		}

		finally {
			setShowBackdrop(false);
		}
	};

	const handleInputChange = (e) => {
		setMessage(undefined);

		const { name, value } = e.target;
		setFormData((prevData) => ({ ...prevData, [name]: value }));
	};

	const handleClose = (event, reason) => {
		if (["backdropclick", "escapekeydown"].includes(reason.toLowerCase())) {
			return;
		}

		setOpen(false);
		window.location.href = "/";
	};

	const [showPassword, setShowPassword] = useState(false);

	const handleClickShowPassword = () => {
		setShowPassword(!showPassword);
	};

	const registerPath = `${ApplicationPaths.Register}`;
	const [showBackdrop, setShowBackdrop] = useState(false);
	
	const formControlStyle = {
		margin: '0.5rem',
		width: '100%',
		'& .MuiInputLabel-root': {
			transform: 'translate(0, -1.5px) scale(0.75)',
			transformOrigin: 'top left'
		},
		'& .MuiInputLabel-root.Mui-focused': {
			transform: 'translate(0, -1.5px) scale(0.75)',
		},
		'& .MuiInputBase-root': {
			marginTop: '16px'
		}
	};

	switch (action) {
		case LoginActions.Login:
			return (
				<Dialog open={open} onClose={handleClose}>
					<Backdrop
						sx={(theme) => ({
							color: "#fff",
							zIndex: theme.zIndex.drawer + 1,
						})}
						open={showBackdrop}
					>
						<CircularProgress color="inherit" />
					</Backdrop>

					<DialogTitle>Login</DialogTitle>

					<DialogContent>
						<Grid
							container
							spacing={2}
							direction="row"
							justifyContent="center"
							alignItems="center"
						>
							{/*register*/}
							<Grid item xs={10} sx={{ textAlign: "right" }}>
								<Button
									variant="outlined"
									component={Link}
									to={registerPath}
								>
									Register
								</Button>
							</Grid>

							{/*credentials*/}
							<Grid item xs={10}>
								<FormControl
									error={!!message}
									variant='standard'
									sx={formControlStyle}
								>

								<TextField
                                    id="Input_Email"
									autoFocus
									label="Email"
									InputLabelProps={{
										shrink: true,
										style: {
											color: "black",
										},
									}}
									name="email"
									value={formData.email}
									onChange={handleInputChange}
									required
									fullWidth
									margin="normal"
									variant="standard"
								/>
								<TextField
                                    id="Input_Password"
									label="Password"
									InputLabelProps={{
										shrink: true,
										style: {
											color: "black",
										},
									}}
									name="password"
									type={
										showPassword ? "text" : "password"
									}
									value={formData.password}
									onChange={handleInputChange}
									required
									fullWidth
									margin="normal"
									variant="standard"
									InputProps={{
										endAdornment: (
											<InputAdornment position="end">
												<IconButton
													onClick={
														handleClickShowPassword
													}
													edge="end"
												>
													{showPassword ? (
														<VisibilityOff />
													) : (
														<Visibility />
													)}
												</IconButton>
											</InputAdornment>
										),
									}}
									/>
									{message && <FormHelperText>{message}</FormHelperText>}
								</FormControl>
							</Grid>

							{/*forgot password*/}
							<Grid item xs={10} sx={{ textAlign: "right" }}>
								<Link
									to="/forgotCredentials"
									onClick={(e) => {
										e.preventDefault();
										// Handle the navigation or action here
										// For example:
										window.location.href =
											"/forgotCredentials"; // If using useNavigate hook
									}}
								>
									Forgot Password?
								</Link>
							</Grid>
						</Grid>
					</DialogContent>

					<DialogActions>
						<Grid
							container
							spacing={2}
							direction="row"
							justifyContent="center"
							alignItems="center"
						>
							{/*login button*/}
							<Grid item xs={10}>
								<Button
                                    id="btnLogin"
									type="button"
									onClick={handleSubmit}
									variant="contained"
									fullWidth
									sx={{
										"&:focus": {
											backgroundColor:
												"#253746 !important", // Keep the default background color on focus
										},
									}}
								>
									Login
								</Button>
							</Grid>

							{/*marketing*/}
							<Grid item xs={10} sx={{ marginTop: "2rem" }}>
								<Typography variant="h5">
									File, track and review your Epiq
									securities class action claims quickly
									and easily with EpiqFiling.
								</Typography>
							</Grid>

							{/*learn more button*/}
							<Grid
								item
								xs={10}
								sx={{ marginBottom: "2rem" }}
							>
								<Button
									variant="outlined"
									href={learnHowUrl}
									target="_blank"
									rel="noopener noreferrer"
								>
									Learn How
								</Button>
							</Grid>
						</Grid>
					</DialogActions>
				</Dialog>
			);
		case LoginActions.LoginCallback:
			return (
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						height: "100vh",
					}}
				>
					<Grid
						container
						spacing={0}
						align="center"
						justify="center"
						direction="column"
					>
						<Grid item>
							<CircularProgress color="inherit" />
							<br />
							<Typography sx={{ color: "#000" }} variant="h4">
								Processing login...
							</Typography>
						</Grid>
					</Grid>
				</div>
			);
		case LoginActions.Profile:
		case LoginActions.Register:
			return <div></div>;
		default:
			throw new Error(`Invalid action '${action}'`);
	}
	
};

export default Login;
